import React, { useEffect, useState } from "react";
import { Autocomplete, Group, Modal, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Alert, Button, ConfirmModal, spacePx } from "@xplatform/design-system";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { CatalogModel } from "@models/providerCatalog";
import { providerCatalogApi } from "@services/providerCatalogApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import DeprecationBadge from "./DeprecationBadge";

interface DeprecationModalProps {
  model: CatalogModel | null;
  providerLabel?: string;
  /** Models of the same provider and capability, as replacement suggestions. */
  replacements: string[];
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Record when a provider retires a model, read from its announcement: the retire
 * date, what to move to, a note and the link. Editing sends the version it read, so
 * two admins never silently overwrite each other.
 */
const DeprecationModal: React.FC<DeprecationModalProps> = ({ model, providerLabel, replacements, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [retireOn, setRetireOn] = useState<Date | null>(null);
  const [replacement, setReplacement] = useState("");
  const [note, setNote] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!model) return;
    const d = model.deprecation;
    setRetireOn(d ? dayjs(d.retire_on).toDate() : null);
    setReplacement(d?.replacement ?? "");
    setNote(d?.note ?? "");
    setSourceUrl(d?.source_url ?? "");
    setError(null);
  }, [model]);

  if (!model) return null;
  const existing = model.deprecation;
  const urlInvalid = Boolean(sourceUrl) && !/^https?:\/\//.test(sourceUrl);

  const run = async (work: () => Promise<unknown>) => {
    setSaving(true);
    setError(null);
    try {
      await work();
      onChanged();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const save = () => run(() => providerCatalogApi().saveDeprecation(model.provider_key, model.model_id, {
    retire_on: dayjs(retireOn).format("YYYY-MM-DD"),
    replacement: replacement.trim() || null,
    note: note.trim() || null,
    source_url: sourceUrl.trim() || null,
    expected_updated_at: existing?.updated_at ?? null,
  }));

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      padding="xl"
      portalProps={{ target: "#admin-portal-root" }}
      title={
        <Stack gap={spacePx[1]}>
          <Text fw={600}>{t(existing ? "admin.models.deprecation.editTitle" : "admin.models.deprecation.addTitle")}</Text>
          <Group gap="xs">
            <Text size="sm" c="dimmed">{providerLabel ?? model.provider_key}</Text>
            <Text size="sm" ff="monospace">{model.model_id}</Text>
            {existing && <DeprecationBadge deprecation={existing} />}
          </Group>
        </Stack>
      }
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">{t("admin.models.deprecation.intro")}</Text>
        {error && <Alert color="red">{error}</Alert>}
        <DatePickerInput
          label={t("admin.models.deprecation.retireOn")}
          description={t("admin.models.deprecation.retireOnHint")}
          value={retireOn}
          onChange={setRetireOn}
          required
        />
        <Autocomplete
          label={t("admin.models.deprecation.replacement")}
          description={t("admin.models.deprecation.replacementHint")}
          data={replacements.filter((m) => m !== model.model_id)}
          value={replacement}
          onChange={setReplacement}
          comboboxProps={{ withinPortal: false }}
        />
        <TextInput
          label={t("admin.models.deprecation.sourceUrl")}
          placeholder="https://"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.currentTarget.value)}
          error={urlInvalid ? t("admin.models.deprecation.sourceUrlInvalid") : undefined}
        />
        <Textarea
          label={t("admin.models.deprecation.note")}
          autosize
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        {existing?.updated_by && (
          <Text size="xs" c="dimmed">
            {t("admin.models.deprecation.lastChanged", { by: existing.updated_by, at: dayjs(existing.updated_at).format("YYYY-MM-DD HH:mm") })}
          </Text>
        )}
        <Group justify="space-between">
          {existing ? (
            <Button variant="subtle" color="red" onClick={() => setConfirmDelete(true)} disabled={saving}>
              {t("admin.models.deprecation.remove")}
            </Button>
          ) : <span />}
          <Button onClick={save} loading={saving} disabled={!retireOn || urlInvalid}>
            {t("admin.models.deprecation.save")}
          </Button>
        </Group>
      </Stack>
      <ConfirmModal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          void run(() => providerCatalogApi().deleteDeprecation(model.provider_key, model.model_id));
        }}
        title={t("admin.models.deprecation.removeTitle")}
        message={t("admin.models.deprecation.removeImpact")}
        confirmLabel={t("admin.models.deprecation.remove")}
        cancelLabel={t("common.cancel")}
        portalProps={{ target: "#admin-portal-root" }}
        danger
      />
    </Modal>
  );
};

export default DeprecationModal;
